export const EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE = 'data-placeholder';
export const EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE = 'data-fill-field';

export type EmailTemplateChip =
  { readonly kind: 'PLACEHOLDER'; readonly key: string } | { readonly kind: 'FILL_FIELD'; readonly label: string };

export type EmailTemplateMarkupToken =
  | { readonly kind: 'open'; readonly tag: string; readonly attributes: Readonly<Record<string, string>> }
  | { readonly kind: 'close'; readonly tag: string }
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'chip'; readonly chip: EmailTemplateChip };

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

const ENTITY_PATTERN = /&(?:#x([0-9a-fA-F]+)|#([0-9]+)|([a-zA-Z]+));/g;
const TAG_PATTERN = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^'">])*)>/g;
const ATTRIBUTE_PATTERN = /([^\s"'<>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

const decodeCodePoint = (codePoint: number, literal: string): string => {
  if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return literal;

  return String.fromCodePoint(codePoint);
};

const decodeEntities = (value: string): string =>
  value.replace(ENTITY_PATTERN, (literal, hex?: string, decimal?: string, named?: string) => {
    if (hex !== undefined) return decodeCodePoint(Number.parseInt(hex, 16), literal);

    if (decimal !== undefined) return decodeCodePoint(Number.parseInt(decimal, 10), literal);

    return NAMED_ENTITIES[named ?? ''] ?? literal;
  });

const parseAttributes = (source: string): Record<string, string> => {
  const attributes: Record<string, string> = {};

  for (const match of source.matchAll(ATTRIBUTE_PATTERN)) {
    const name = (match[1] ?? '').toLowerCase();
    attributes[name] = decodeEntities(match[2] ?? match[3] ?? match[4] ?? '');
  }

  return attributes;
};

const chipOf = (attributes: Readonly<Record<string, string>>): EmailTemplateChip | null => {
  const placeholder = attributes[EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE];

  if (placeholder !== undefined) return { kind: 'PLACEHOLDER', key: placeholder };
  const fillField = attributes[EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE];

  if (fillField !== undefined) return { kind: 'FILL_FIELD', label: fillField };

  return null;
};

/**
 * Tags lower-cased; `<br>` / `<br/>` emit one `open` token, no `close`; comments dropped;
 * a chip span and everything up to its matching `</span>` become one `chip` token.
 */
export function tokenizeEmailTemplateMarkup(markup: string): EmailTemplateMarkupToken[] {
  const tokens: EmailTemplateMarkupToken[] = [];
  let cursor = 0;
  let chipDepth = 0;
  let chip: EmailTemplateChip | null = null;

  const pushText = (end: number) => {
    if (chipDepth > 0 || end <= cursor) return;
    tokens.push({ kind: 'text', text: decodeEntities(markup.slice(cursor, end)) });
  };

  for (const match of markup.matchAll(TAG_PATTERN)) {
    const index = match.index ?? 0;
    pushText(index);
    cursor = index + match[0].length;

    const tag = match[2]?.toLowerCase();

    if (tag === undefined) continue;
    const closing = match[1] === '/';

    if (chipDepth > 0) {
      if (tag !== 'span') continue;
      chipDepth += closing ? -1 : 1;

      if (chipDepth === 0 && chip !== null) {
        tokens.push({ kind: 'chip', chip });
        chip = null;
      }

      continue;
    }

    if (closing) {
      tokens.push({ kind: 'close', tag });
      continue;
    }

    const attributes = parseAttributes(match[3] ?? '');
    const found = tag === 'span' ? chipOf(attributes) : null;

    if (found !== null) {
      chip = found;
      chipDepth = 1;

      if (/\/\s*$/.test(match[3] ?? '')) {
        tokens.push({ kind: 'chip', chip: found });
        chip = null;
        chipDepth = 0;
      }

      continue;
    }

    tokens.push({ kind: 'open', tag, attributes });
  }

  pushText(markup.length);

  if (chipDepth > 0 && chip !== null) tokens.push({ kind: 'chip', chip });

  return tokens;
}

/** Every chip span in document order. A chip's own inner text, if any, is ignored. */
export function listEmailTemplateChips(markup: string): EmailTemplateChip[] {
  const chips: EmailTemplateChip[] = [];

  for (const token of tokenizeEmailTemplateMarkup(markup)) {
    if (token.kind === 'chip') chips.push(token.chip);
  }

  return chips;
}

const visibleText = (markup: string): string =>
  tokenizeEmailTemplateMarkup(markup)
    .map((token) => (token.kind === 'text' ? token.text : ''))
    .join('');

/** Visible characters (code points): tags stripped, entities decoded, chips count 0. */
export function countEmailTemplateVisibleCharacters(markup: string): number {
  return Array.from(visibleText(markup)).length;
}

/** True when the markup holds no non-whitespace visible text and no chip. */
export function isEmailTemplateMarkupEmpty(markup: string): boolean {
  return listEmailTemplateChips(markup).length === 0 && visibleText(markup).trim() === '';
}
